import { useCallback } from "react";

import toast from "react-hot-toast";
import { Trans } from "react-i18next";

import styled from "styled-components";

import { Button } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch } from "store";
import util from "utils/utils";

import type {
  InvitationAcceptResponse,
  NotificationContentProps,
} from "../types";

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

export const IdeaBusinessProfileInvitedActions = ({
  events = [],
  invitations = [],
  onAccept,
  onReject,
}: NotificationContentProps) => {
  const invitationId = events[0]?.invitation;
  const invitationExists =
    invitationId ?
      invitations.find((i) => i._id === invitationId && !i.acceptedAt)
    : undefined;

  const dispatch = useAppDispatch();

  const acceptInvite = useCallback(() => {
    if (!invitationId) return;
    api.invitations.accept(
      invitationId,
      (data: InvitationAcceptResponse) => {
        if (data.permissions) {
          Object.entries(data.permissions).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              dispatch(actions.user.updatePermissions(key, value));
            }
          });
        }
        toast.success("Business profile joined");
        onAccept?.(invitationId);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [invitationId, onAccept, dispatch]);

  const rejectInvite = useCallback(() => {
    if (!invitationId) return;
    api.invitations.reject(
      invitationId,
      () => {
        onReject?.(invitationId);
        toast.success("Group invitation rejected");
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [invitationId, onReject]);

  if (!invitationExists) return null;
  return (
    <ButtonContainer>
      <Button
        icon="check"
        color="green"
        size="small"
        onClick={acceptInvite}
        style={{ marginBottom: 5 }}
      />
      <Button basic icon="trash" size="small" onClick={rejectInvite} />
    </ButtonContainer>
  );
};

export const IdeaBusinessProfileInvited = ({
  events = [],
  ownerIdeaBusinessProfile,
}: NotificationContentProps) => {
  return (
    <Trans
      i18nKey="notifications.ideaBusinessProfileInvited"
      values={{
        name: util.getEventNames(events ?? []),
        profileName: ownerIdeaBusinessProfile?.name || "Deleted",
      }}
    />
  );
};
